import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import FinancialControlService, {
  FinancialProject,
} from '../../services/financialControlService';
import { format } from 'date-fns';

const FinancialControlProjects: React.FC = () => {
  const [projects, setProjects] = useState<FinancialProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<FinancialProject | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'paused' | 'completed' | 'archived',
    start_date: '',
    end_date: '',
    currency: 'BRL',
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await FinancialControlService.listProjects();
      if (response.success && response.data) {
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project?: FinancialProject) => {
    if (project) {
      setEditingProject(project);
      setProjectForm({
        name: project.name,
        description: project.description || '',
        status: project.status,
        start_date: project.start_date ? format(new Date(project.start_date), 'yyyy-MM-dd') : '',
        end_date: project.end_date ? format(new Date(project.end_date), 'yyyy-MM-dd') : '',
        currency: project.currency,
      });
    } else {
      setEditingProject(null);
      setProjectForm({
        name: '',
        description: '',
        status: 'active',
        start_date: '',
        end_date: '',
        currency: 'BRL',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleSave = async () => {
    if (!projectForm.name.trim()) {
      setSnackbar({ open: true, message: 'Nome do projeto é obrigatório', severity: 'error' });
      return;
    }

    try {
      const projectData: any = {
        name: projectForm.name.trim(),
        description: projectForm.description || undefined,
        status: projectForm.status,
        start_date: projectForm.start_date || undefined,
        end_date: projectForm.end_date || undefined,
        currency: projectForm.currency,
      };

      let response;
      if (editingProject) {
        response = await FinancialControlService.updateProject(editingProject.id, projectData);
      } else {
        response = await FinancialControlService.createProject(projectData);
      }

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: editingProject ? 'Projeto atualizado com sucesso' : 'Projeto criado com sucesso', 
          severity: 'success' 
        });
        handleCloseDialog();
        loadProjects();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao salvar projeto', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar projeto', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este projeto? Todas as receitas e despesas relacionadas serão mantidas.')) return;

    try {
      const response = await FinancialControlService.deleteProject(id);
      if (response.success) {
        setSnackbar({ open: true, message: 'Projeto deletado com sucesso', severity: 'success' });
        loadProjects();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao deletar projeto', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao deletar projeto', severity: 'error' });
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: 'Ativo',
      paused: 'Pausado',
      completed: 'Concluído',
      archived: 'Arquivado',
    };
    return labels[status] || status;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Projetos Financeiros
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} color="primary">
            Novo Projeto
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadProjects} disabled={loading}>
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data Início</TableCell>
              <TableCell>Data Fim</TableCell>
              <TableCell>Moeda</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {project.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {project.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(project.status)} 
                    size="small" 
                    color={getStatusColor(project.status) as any}
                  />
                </TableCell>
                <TableCell>{project.start_date ? formatDate(project.start_date) : '-'}</TableCell>
                <TableCell>{project.end_date ? formatDate(project.end_date) : '-'}</TableCell>
                <TableCell>{project.currency}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(project)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(project.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">Nenhum projeto encontrado</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Criar/Editar Projeto */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingProject ? 'Editar Projeto' : 'Novo Projeto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Nome do Projeto"
              fullWidth
              required
              value={projectForm.name}
              onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
              placeholder="Ex: Curso de Marketing Digital"
            />

            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={3}
              value={projectForm.description}
              onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
              placeholder="Descrição do projeto..."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={projectForm.status}
                    onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value as any })}
                    label="Status"
                  >
                    <MenuItem value="active">Ativo</MenuItem>
                    <MenuItem value="paused">Pausado</MenuItem>
                    <MenuItem value="completed">Concluído</MenuItem>
                    <MenuItem value="archived">Arquivado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Moeda</InputLabel>
                  <Select
                    value={projectForm.currency}
                    onChange={(e) => setProjectForm({ ...projectForm, currency: e.target.value })}
                    label="Moeda"
                  >
                    <MenuItem value="BRL">BRL (Real)</MenuItem>
                    <MenuItem value="USD">USD (Dólar)</MenuItem>
                    <MenuItem value="EUR">EUR (Euro)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de Início"
                  fullWidth
                  type="date"
                  value={projectForm.start_date}
                  onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de Fim"
                  fullWidth
                  type="date"
                  value={projectForm.end_date}
                  onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingProject ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialControlProjects;


